import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { loginSchema } from '@wingseat/shared';
import { useAuth } from '../auth/auth-context';
import { ApiError, api } from '../lib/api-client';

export function LoginPage() {
  const navigate = useNavigate();
  const { refetch } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const mutation = useMutation({
    mutationFn: async () => {
      const parsed = loginSchema.safeParse({ email, password });
      if (!parsed.success) {
        const errs: Record<string, string> = {};
        for (const issue of parsed.error.issues) {
          const key = issue.path[0]?.toString();
          if (key && !errs[key]) errs[key] = issue.message;
        }
        setFieldErrors(errs);
        throw new Error('validation');
      }
      setFieldErrors({});
      await api.post('/auth/login', parsed.data);
      await refetch();
      navigate('/app/flights', { replace: true });
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate();
  };

  const apiErrMessage =
    mutation.error instanceof ApiError
      ? mutation.error.message
      : mutation.error && mutation.error.message !== 'validation'
        ? 'Login fehlgeschlagen'
        : null;

  return (
    <div className="flex min-h-full items-center justify-center px-6 py-12">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm space-y-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-6 shadow-xl"
      >
        <header className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold">WingSeat</h1>
          <p className="text-sm text-slate-400">Mit deinem Konto anmelden</p>
        </header>

        <div className="space-y-1.5">
          <label htmlFor="email" className="block text-sm font-medium">
            E-Mail
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-base placeholder:text-slate-500 focus:border-brand-500 focus:outline-none"
            placeholder="dein.name@example.com"
            required
          />
          {fieldErrors['email'] && (
            <p className="text-xs text-rose-400">{fieldErrors['email']}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <label htmlFor="password" className="block text-sm font-medium">
            Passwort
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-base placeholder:text-slate-500 focus:border-brand-500 focus:outline-none"
            required
          />
          {fieldErrors['password'] && (
            <p className="text-xs text-rose-400">{fieldErrors['password']}</p>
          )}
        </div>

        {apiErrMessage && (
          <div className="rounded-lg border border-rose-900 bg-rose-950/60 px-3 py-2 text-sm text-rose-200">
            {apiErrMessage}
          </div>
        )}

        <button
          type="submit"
          disabled={mutation.isPending}
          className="w-full rounded-lg bg-brand-600 px-4 py-2.5 text-base font-medium text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-60"
        >
          {mutation.isPending ? 'Anmelden …' : 'Anmelden'}
        </button>

        <div className="text-center">
          <Link
            to="/password-reset"
            className="text-xs text-brand-400 hover:underline"
          >
            Passwort vergessen?
          </Link>
        </div>
      </form>
    </div>
  );
}
